import { createApp, ref } from 'vue';
import ee from 'event-emitter';
import i18NextVue from 'i18next-vue';
import Vue3Toastify from 'vue3-toastify';
import { init as initSentry } from '@sentry/vue';
import * as Sentry from '@sentry/browser';
import PrimeVue from 'primevue/config';
import Ripple from 'primevue/ripple';
import Tooltip from 'primevue/tooltip';
import ConfirmationService from 'primevue/confirmationservice';
import Aura from '@primeuix/themes/aura';

import i18next from './i18n';
import piniaStore from './pinia.store';
import { DialogPlugin } from './components/Dialog';

import { APP_MOUNT_SELECTOR, APP_ENV } from './constants/main';
import loadConfig, { type TConfigEnv } from './services/ConfigService';
import { provideAppEnvironment } from './services/ApiClient';
import { permissionDirective } from '@/modules/acl/aclDirective';

(async function main(): Promise<void> {
  try {
    const appConfigTag = document.getElementById('app-config');
    const envConfigPath = appConfigTag?.getAttribute('src') || null;

    if (typeof envConfigPath !== 'string') {
      throw new Error('App configuration script tag not found');
    }

    const envConfig = await loadConfig<TConfigEnv>(envConfigPath);

    provideAppEnvironment(envConfig);
    Object.assign(window, {
      // disable hydration mismatch warnings - needed for Server Side Rendering only
      __VUE_PROD_HYDRATION_MISMATCH_DETAILS__: false,
    });

    // they depend on the app environment
    // end vue global config
    const [
      { default: App },
      { default: router },
    ] = await Promise.all([
      import('./App.vue'),
      import('./modules/router'),
    ]);

    const appRoot = document.querySelector(APP_MOUNT_SELECTOR);

    if (!appRoot) {
      throw new Error('Root element not found');
    }

    const theApp = createApp(App)
      .provide(APP_ENV, envConfig)
      .provide('emitter', ee())
      .use(router)
      .use(piniaStore)
      .use(Vue3Toastify, { clearOnUrlChange: false })
      .use(i18NextVue, { i18next })
      .use(DialogPlugin, (dialogApp) => {
        dialogApp.use(i18NextVue, { i18next });
      });

    const isRouterReady = ref(false);
    router.isReady().then(() => {
      isRouterReady.value = true;
    });
    theApp.provide('isRouterReady', isRouterReady);

    if (envConfig.SENTRY_DNS) {
      initSentry({
        app: theApp,
        dsn: envConfig.SENTRY_DNS,
        environment: envConfig.SENTRY_ENV,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration(),
        ],
        tracesSampleRate: 1.0,
        tracePropagationTargets: ['localhost'],
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        sendDefaultPii: true,
      });
    }

    theApp.use(PrimeVue, {
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.p-dark',
          cssLayer: true,
        },
      },
    });
    theApp.directive('ripple', Ripple);
    theApp.directive('tooltip', Tooltip);
    theApp.directive('permission', permissionDirective);
    theApp.use(ConfirmationService);
    theApp.mount(appRoot);
  } catch (error) {
    console.error('Error during app initialization:', error);
  }
})();
